import { FIXTURE_RELEASE, FIXTURE_WORKS } from "./fixture";
import type { AudioManifest, Author, BrowseWork, Manifest, PoetryWork } from "./types";

const DATA_ORIGIN = (import.meta.env.VITE_POETRY_DATA_ORIGIN || "https://poetrydb.xhalo.co").replace(/\/+$/, "");
const API_ORIGIN = (import.meta.env.VITE_POETRY_API_ORIGIN || "https://corpus.xhalo.co").replace(/\/+$/, "");
const EXPLICIT_RELEASE = import.meta.env.VITE_POETRY_RELEASE_VERSION || "";
const USE_FIXTURE = import.meta.env.VITE_USE_FIXTURE === "true";
const shardCache = new Map<string, Promise<PoetryWork[]>>();
const browseIndexCache = new Map<string, Promise<ShardIndex>>();

type ShardIndex = { shards: Array<{ path: string; count: number }> };
export type BrowseCursor = { shard: number; offset: number };
export type BrowseBatch = { works: BrowseWork[]; nextShard: BrowseCursor | null };
const BROWSE_PAGE_SIZE = 100;

async function readJson<T>(path: string): Promise<T> {
  const response = await fetch(`${DATA_ORIGIN}/${path}`, { headers: { accept: "application/json" } });
  if (!response.ok) throw new Error(`Data request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export async function releaseVersion() {
  if (USE_FIXTURE) return FIXTURE_RELEASE;
  if (EXPLICIT_RELEASE) return EXPLICIT_RELEASE;
  const latest = await readJson<{ version: string }>("latest.json");
  if (!latest.version) throw new Error("Active Poetry release is unavailable.");
  return latest.version;
}

export async function browse(): Promise<BrowseWork[]> {
  return (await browseBatch()).works;
}

export async function browseBatch(
  cursor: BrowseCursor = { shard: 0, offset: 0 },
): Promise<BrowseBatch> {
  if (USE_FIXTURE) {
    return { works: cursor.shard === 0 && cursor.offset === 0 ? FIXTURE_WORKS : [], nextShard: null };
  }
  const version = await releaseVersion();
  const releaseRoot = `releases/${encodeURIComponent(version)}`;
  let index = browseIndexCache.get(version);
  if (!index) {
    index = readJson<ShardIndex>(`${releaseRoot}/browse/index.json`);
    browseIndexCache.set(version, index);
  }
  try {
    const shards = (await index).shards;
    const selected = shards[cursor.shard];
    if (!selected) return { works: [], nextShard: null };
    const shardWorks = await readJson<BrowseWork[]>(`${releaseRoot}/${selected.path}`);
    const works = shardWorks.slice(cursor.offset, cursor.offset + BROWSE_PAGE_SIZE);
    const nextOffset = cursor.offset + works.length;
    return {
      works,
      nextShard:
        nextOffset < shardWorks.length
          ? { shard: cursor.shard, offset: nextOffset }
          : cursor.shard + 1 < shards.length
            ? { shard: cursor.shard + 1, offset: 0 }
            : null,
    };
  } catch {
    if (cursor.shard > 0 || cursor.offset > 0) return { works: [], nextShard: null };
    const works = await readJson<BrowseWork[]>(`${releaseRoot}/browse.json`);
    return { works: works.slice(0, BROWSE_PAGE_SIZE), nextShard: null };
  }
}

export async function authors(): Promise<Author[]> {
  if (USE_FIXTURE) {
    return [...new Map(FIXTURE_WORKS.flatMap((work) => work.creators).map((creator) => [creator.id, creator])).values()];
  }
  const version = await releaseVersion();
  const releaseRoot = `releases/${encodeURIComponent(version)}`;
  try {
    const people = await readJson<Author[]>(`${releaseRoot}/authors/summary.json`);
    return people.sort((left, right) => (right.work_count || 0) - (left.work_count || 0)).slice(0, 500);
  } catch {
    return readJson(`${releaseRoot}/authors.json`);
  }
}

export async function manifest(): Promise<Manifest> {
  if (USE_FIXTURE) return { version: FIXTURE_RELEASE, fixture_only: true, activation_eligible: false, state: "candidate", counts: { works: FIXTURE_WORKS.length, authors: 3, languages: { zh: 2, en: 1 } } };
  const version = await releaseVersion();
  return readJson(`releases/${encodeURIComponent(version)}/manifest.json`);
}

export async function workById(id: string): Promise<PoetryWork | null> {
  if (USE_FIXTURE) return FIXTURE_WORKS.find((work) => work.id === id) || null;
  const version = await releaseVersion();
  const releaseRoot = `releases/${encodeURIComponent(version)}`;
  const prefix = id.split("_", 2).at(-1)?.slice(0, 2).toLowerCase() || "00";
  let lookup: Record<string, { shard: string; offset: number }>;
  try {
    lookup = await readJson(`${releaseRoot}/lookup/works/${prefix}.json`);
  } catch {
    lookup = await readJson(`${releaseRoot}/lookup/works.json`);
  }
  const location = lookup[id];
  if (!location) return null;
  const key = `${version}/${location.shard}`;
  let shard = shardCache.get(key);
  if (!shard) {
    shard = readJson(`${releaseRoot}/${location.shard}`);
    shardCache.set(key, shard);
  }
  return (await shard)[location.offset] || null;
}

export async function searchWorks(query: string, language = "all"): Promise<BrowseWork[]> {
  if (USE_FIXTURE) return filterWorks(FIXTURE_WORKS, query, language, "all");
  const parameters = new URLSearchParams({ q: query, mode: "fts", limit: "20" });
  if (language !== "all") parameters.set("language", language);
  const response = await fetch(`${API_ORIGIN}/v1/public/poetry/search?${parameters}`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Search request failed (${response.status})`);
  const payload = await response.json() as {
    ok?: boolean;
    data?: { hits?: Array<Record<string, unknown>> };
  };
  const remoteMatches = (payload.data?.hits || []).map((hit) => {
    const citation = (hit.citation || {}) as Record<string, unknown>;
    return {
      id: String(hit.id),
      edition_id: String(hit.editionId),
      title: String(hit.title),
      language: String(hit.language),
      creators: (hit.creators || []) as BrowseWork["creators"],
      citation: {
        id: String(citation.id || ""),
        label: String(citation.label || hit.title),
        source_locator: String(citation.sourceLocator || ""),
      },
    };
  });
  const curatedMatches = filterWorks(FIXTURE_WORKS, query, language, "all");
  return mergeWorksById(curatedMatches, remoteMatches);
}

export function mergeWorksById(...collections: BrowseWork[][]): BrowseWork[] {
  return [...new Map(collections.flat().map((work) => [work.id, work])).values()];
}

const AUDIO_POLL_DELAYS_MS = [0, 1_000, 2_000, 4_000, 8_000] as const;

function isAudioManifest(value: unknown): value is AudioManifest {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AudioManifest>;
  return typeof candidate.workId === "string" && typeof candidate.editionId === "string"
    && Number.isInteger(candidate.currentUnit) && Array.isArray(candidate.units);
}

export async function prepareCanonicalAudio(
  workId: string,
  unit = 0,
  options: { delays?: readonly number[]; sleep?: (milliseconds: number) => Promise<void> } = {},
): Promise<AudioManifest> {
  const delays = options.delays || AUDIO_POLL_DELAYS_MS;
  const sleep = options.sleep || ((milliseconds: number) => new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds)));
  let last: AudioManifest | null = null;
  for (const delay of delays) {
    if (delay > 0) await sleep(delay);
    const response = await fetch(
      `${API_ORIGIN}/v1/public/poetry/works/${encodeURIComponent(workId)}/audio/manifest?unit=${unit}`,
      { method: "POST", headers: { accept: "application/json" } },
    );
    const payload = await response.json().catch(() => null) as unknown;
    const candidate = isAudioManifest(payload)
      ? payload
      : payload && typeof payload === "object" && "data" in payload && isAudioManifest((payload as { data?: unknown }).data)
        ? (payload as { data: AudioManifest }).data
        : null;
    if (!candidate) throw new Error(`Audio request failed (${response.status})`);
    last = candidate;
    if (candidate.units[unit]?.status !== "pending") return candidate;
  }
  if (!last) throw new Error("Audio manifest is unavailable.");
  return { ...last, pollingExhausted: true };
}

export function filterWorks(works: BrowseWork[], query: string, language: string, author: string) {
  const needle = query.normalize("NFKC").trim().toLocaleLowerCase();
  return works.filter((work) => {
    if (language !== "all" && !work.language.toLowerCase().startsWith(language)) return false;
    if (author !== "all" && !work.creators.some((creator) => creator.id === author)) return false;
    return !needle || work.title.toLocaleLowerCase().includes(needle) || work.creators.some((creator) => creator.name.toLocaleLowerCase().includes(needle));
  });
}
